import { Mp3Encoder } from '@breezystack/lamejs'

const SUPPORTED_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000]
const BLOCK = 1152
const BITRATE = 128

type AudioCtor = typeof AudioContext

export function isMp3Name(name?: string) {
  return /\.mp3(\?.*)?$/i.test((name || '').trim())
}

function getAudioContext(): AudioContext {
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor }
  const Ctor = w.AudioContext || w.webkitAudioContext
  if (!Ctor) throw new Error('Este navegador não consegue converter áudio para MP3.')
  return new Ctor()
}

function decode(ctx: AudioContext, data: ArrayBuffer) {
  return new Promise<AudioBuffer>((resolve, reject) => {
    ctx.decodeAudioData(data, resolve, (err) => reject(err || new Error('decode')))
  })
}

async function resample(buffer: AudioBuffer, rate: number) {
  const length = Math.ceil(buffer.duration * rate)
  const offline = new OfflineAudioContext(buffer.numberOfChannels, length, rate)
  const source = offline.createBufferSource()
  source.buffer = buffer
  source.connect(offline.destination)
  source.start()
  return offline.startRendering()
}

function floatTo16(input: Float32Array) {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out
}

function copyBytes(buf: Int8Array) {
  return new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.length))
}

/** Converte qualquer áudio que o navegador consiga ler (WAV, OGG, M4A, AAC, FLAC...) para MP3. */
export async function convertToMp3(source: Blob): Promise<Blob> {
  const ctx = getAudioContext()
  let decoded: AudioBuffer
  try {
    decoded = await decode(ctx, await source.arrayBuffer())
  } catch {
    throw new Error('Não foi possível ler este áudio para converter em MP3.')
  } finally {
    void ctx.close().catch(() => undefined)
  }

  if (!SUPPORTED_RATES.includes(decoded.sampleRate)) {
    decoded = await resample(decoded, 44100)
  }

  const channels = decoded.numberOfChannels > 1 ? 2 : 1
  const left = floatTo16(decoded.getChannelData(0))
  const right = channels === 2 ? floatTo16(decoded.getChannelData(1)) : null
  const encoder = new Mp3Encoder(channels, decoded.sampleRate, BITRATE)
  const chunks: BlobPart[] = []

  for (let i = 0; i < left.length; i += BLOCK) {
    const l = left.subarray(i, i + BLOCK)
    const buf = right ? encoder.encodeBuffer(l, right.subarray(i, i + BLOCK)) : encoder.encodeBuffer(l)
    if (buf.length) chunks.push(copyBytes(buf))
    if ((i / BLOCK) % 400 === 0) await new Promise((resolve) => setTimeout(resolve, 0))
  }
  const end = encoder.flush()
  if (end.length) chunks.push(copyBytes(end))

  return new Blob(chunks, { type: 'audio/mpeg' })
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 5000)
}

function safeName(value: string) {
  return value.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120) || 'musica'
}

interface DownloadableTrack {
  title: string
  artist?: string
  fileUrl: string
  fileName?: string
}

/** Baixa a música sempre em MP3, convertendo se o arquivo guardado estiver noutro formato. */
export async function downloadTrackAsMp3(track: DownloadableTrack) {
  const base = safeName([track.artist, track.title].filter(Boolean).join(' - '))
  const filename = `${base}.mp3`
  const knownMp3 = isMp3Name(track.fileName) || isMp3Name(track.fileUrl)

  let blob: Blob
  try {
    const response = await fetch(track.fileUrl)
    if (!response.ok) throw new Error('http')
    blob = await response.blob()
  } catch {
    if (knownMp3) {
      window.open(track.fileUrl, '_blank', 'noopener')
      return
    }
    throw new Error('Não foi possível obter o arquivo da música.')
  }

  if (knownMp3 || blob.type === 'audio/mpeg') {
    saveBlob(new Blob([blob], { type: 'audio/mpeg' }), filename)
    return
  }
  saveBlob(await convertToMp3(blob), filename)
}
